<script setup>
import { computed, ref, watch } from 'vue';
import { t } from '../i18n';
import { linkSegments, splitPost } from '../news';
import { dayLabel, localDateKey, timeLabel } from '../operations';

// Posts by day with chips, links and «ещё» (docs/news/SPEC.md, decisions 17–19, 25): the news screen and
// the instrument screen's news tab (docs/instrument/SPEC.md).
const props = defineProps({
  // Items of GET /news/list or /instruments/{uid}/news
  items: {
    type: Array,
    required: true,
  },
  desktop: {
    type: Boolean,
    default: false,
  },
  // On the news screen a chip adds its tag to the filter; elsewhere chips are plain labels.
  chipsClickable: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['chip']);

const expanded = ref(new Set());
// A new first page collapses everything again.
watch(() => props.items[0]?.id, () => { expanded.value = new Set(); });

// Posts grouped by local day, newest first; the header (emoji, hashtags) is split off into chips.
const days = computed(() => {
  const result = [];
  for (const item of props.items) {
    const key = localDateKey(item.posted_at);
    if (result.length === 0 || result[result.length - 1].key !== key) {
      result.push({ key, items: [] });
    }
    const parts = splitPost(item.text, item.mine);
    result[result.length - 1].items.push({
      ...item,
      ...parts,
      segments: linkSegments(parts.body, item.links),
      mineTags: parts.tags.filter((tag) => tag.mine),
      otherTags: parts.tags.filter((tag) => !tag.mine),
    });
  }
  return result;
});

// Long posts are clamped; a tap shows the whole text.
const LONG_TEXT = 280;

function isLong(item) {
  return item.body.length > LONG_TEXT || item.body.split('\n').length > 6;
}

function toggle(item) {
  const next = new Set(expanded.value);
  if (next.has(item.id)) {
    next.delete(item.id);
  } else {
    next.add(item.id);
  }
  expanded.value = next;
}

const CHIP_MINE = 'rounded-full bg-slate-100 px-2.5 py-0.5 text-[0.8125rem] font-medium text-slate-950 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-400';
const CHIP_OTHER = 'rounded-full bg-slate-800 px-2.5 py-0.5 text-[0.8125rem] text-slate-400 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-400';
</script>

<template>
  <section class="rounded-3xl bg-slate-900 py-2 transition-opacity sm:rounded-lg sm:border sm:border-slate-700 sm:bg-slate-900/40 sm:py-0">
    <div
      v-for="day in days"
      :key="day.key"
    >
      <h2 class="px-5 pb-1 pt-3 text-[0.8125rem] text-slate-500 first-letter:uppercase sm:bg-slate-900/60 sm:py-1.5 sm:text-xs">
        {{ dayLabel(day.key, { full: desktop }) }}
      </h2>
      <ul>
        <li
          v-for="item in day.items"
          :key="item.id"
          class="flex flex-col gap-1.5 border-b border-slate-800/70 px-5 py-3 last:border-b-0"
        >
          <!-- Chips (decision 17): the user's tickers first and white, other hashtags grey, each emoji in a circle on the right -->
          <div
            v-if="item.tags.length > 0 || item.emoji.length > 0"
            class="flex items-start gap-2"
          >
            <div class="flex min-w-0 flex-1 flex-wrap gap-1.5">
              <template
                v-for="tag in [...item.mineTags, ...item.otherTags]"
                :key="tag.label"
              >
                <!-- A tap adds the tag to the filter (or removes it) -->
                <button
                  v-if="chipsClickable"
                  type="button"
                  :class="[tag.mine ? CHIP_MINE : CHIP_OTHER, tag.mine ? '' : 'hover:text-slate-200']"
                  :title="t('news.addToFilter')"
                  @click="emit('chip', tag.label)"
                >
                  {{ tag.label }}
                </button>
                <span
                  v-else
                  :class="tag.mine ? CHIP_MINE : CHIP_OTHER"
                >{{ tag.label }}</span>
              </template>
            </div>
            <div
              v-if="item.emoji.length > 0"
              class="flex shrink-0 gap-1"
            >
              <span
                v-for="(symbol, index) in item.emoji"
                :key="index"
                class="flex size-[26px] items-center justify-center rounded-full bg-slate-800 text-[0.8125rem] leading-none"
              >{{ symbol }}</span>
            </div>
          </div>
          <p
            v-if="item.body || !item.text"
            class="whitespace-pre-line break-words text-[0.9375rem] leading-snug text-slate-100 sm:text-sm"
            :class="isLong(item) && !expanded.has(item.id) ? 'line-clamp-6' : ''"
          >
            <template v-if="item.body">
              <template
                v-for="(segment, index) in item.segments"
                :key="index"
              >
                <!-- Links open in a new tab (decision 25) -->
                <a
                  v-if="segment.url"
                  :href="segment.url"
                  target="_blank"
                  rel="noopener noreferrer"
                  class="text-sky-300 underline decoration-sky-300/40 underline-offset-2 hover:decoration-sky-300"
                >{{ segment.text }}</a>
                <template v-else>
                  {{ segment.text }}
                </template>
              </template>
            </template>
            <template v-else>
              {{ t('news.mediaOnly') }}
            </template>
          </p>
          <!-- Bottom row (decision 19): "show more" on the left, the time in a pill on the right -->
          <div class="flex items-center gap-2">
            <button
              v-if="isLong(item)"
              type="button"
              class="text-[0.8125rem] font-medium text-slate-400 hover:text-slate-200"
              @click="toggle(item)"
            >
              {{ expanded.has(item.id) ? t('news.less') : t('news.more') }}
            </button>
            <span
              v-if="item.edited"
              class="ml-auto text-[0.75rem] text-slate-500"
            >{{ t('news.edited') }}</span>
            <time
              :datetime="item.posted_at"
              class="rounded-full bg-slate-800 px-2.5 py-0.5 text-[0.75rem] tabular-nums text-slate-400"
              :class="item.edited ? '' : 'ml-auto'"
            >{{ timeLabel(item.posted_at) }}</time>
          </div>
        </li>
      </ul>
    </div>
  </section>
</template>
